import { useEffect, useState } from 'react';
import { ScanLine } from 'lucide-react';
import BeforeAfterSlider from './BeforeAfterSlider';
import { ROOM_IMAGE_BEFORE, ROOM_IMAGE_AFTER, ZONES } from '../data/shopFurnitureShowcase';

const ZONE_INTERVAL_MS = 2500;
const MAX_VISIBLE_PRODUCTS = 3;

function ShopTheFurnitureShowcase() {
  const [activeZoneIndex, setActiveZoneIndex] = useState(0);
  const [reduceMotion, setReduceMotion] = useState(false);
  const [sliderPosition, setSliderPosition] = useState(15);

  const activeZone = ZONES[activeZoneIndex];
  const visibleProducts = activeZone.products.slice(0, MAX_VISIBLE_PRODUCTS);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setReduceMotion(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  useEffect(() => {
    if (reduceMotion) return undefined;
    const timer = setInterval(() => {
      setActiveZoneIndex((i) => (i + 1) % ZONES.length);
    }, ZONE_INTERVAL_MS);
    return () => clearInterval(timer);
  }, [reduceMotion]);

  const bboxStyle = {
    '--detect-left': `${activeZone.bbox.left}%`,
    '--detect-top': `${activeZone.bbox.top}%`,
    '--detect-width': `${activeZone.bbox.width}%`,
    '--detect-height': `${activeZone.bbox.height}%`,
  };

  return (
    <section
      className={`shop-furniture-showcase container section${reduceMotion ? ' shop-furniture-showcase--reduced-motion' : ''}`}
      aria-label="Room preview and budget planning"
    >
      <div className="section-heading shop-furniture-showcase__section-heading">
        <h2>
          <span style={{ color: '#237CE4' }}>A</span>I Designs Based On Real Furniture
        </h2>
      </div>

      <div className="shop-furniture-showcase__demo-inner">
          <div className="shop-furniture-showcase__stage">
            <BeforeAfterSlider
              before={ROOM_IMAGE_BEFORE}
              after={ROOM_IMAGE_AFTER}
              initialPosition={15}
              onPositionChange={setSliderPosition}
              loading="lazy"
            />
            <div
              className="shop-furniture-showcase__stage-overlays"
              style={{ clipPath: `inset(0 0 0 ${sliderPosition}%)` }}
              aria-hidden
            >
              <div className="shop-furniture-showcase__scan" />
              <div className="shop-furniture-showcase__detect-box" style={bboxStyle}>
                <span className="ai-target-dot" aria-hidden />
              </div>
            </div>
          </div>

          <div
            key={activeZone.id}
            className="shop-furniture-showcase__product-panel"
            role="region"
            aria-label="Matching products"
          >
            <p className="shop-furniture-showcase__zone-label" aria-live="polite">
              Searching: {activeZone.label}
            </p>
            <header className="shop-furniture-showcase__results-header">
              <ScanLine size={16} strokeWidth={1.65} aria-hidden />
              <span>AI Found {visibleProducts.length} Results</span>
            </header>
            <div className="shop-furniture-showcase__product-grid">
            {visibleProducts.map((product, index) => (
              <article
                key={`${activeZone.id}-${product.brand}-${index}`}
                className="shop-furniture-showcase__product-card"
              >
                <div
                  className={`shop-furniture-showcase__product-thumb shop-furniture-showcase__product-thumb--${product.icon}`}
                >
                  <img
                    src={product.image}
                    alt=""
                    className="shop-furniture-showcase__product-img"
                    loading="lazy"
                    decoding="async"
                    width={192}
                    height={172}
                  />
                </div>
                <div className="shop-furniture-showcase__product-info">
                  <h3 className="shop-furniture-showcase__product-name">{product.name}</h3>
                  <p className="shop-furniture-showcase__product-brand">{product.brand}</p>
                  <p className="shop-furniture-showcase__product-price">{product.price}</p>
                </div>
              </article>
            ))}
            </div>
          </div>
        </div>

        <div className="shop-furniture-showcase__copy">
          <h2 className="shop-furniture-showcase__title">
            See It{' '}
            <span className="shop-furniture-showcase__title-accent shop-furniture-showcase__title-accent--gradient">
              Before You Buy It
            </span>
            .
          </h2>
          <p className="shop-furniture-showcase__tagline">
            Design smarter. Plan your budget with confidence.
          </p>
          <p className="shop-furniture-showcase__desc">
            Preview your real room redesigned before buying furniture or starting renovations.
            Then discover similar products from the design, compare styles and prices, and estimate
            your budget before making a decision.
          </p>
          <ul className="shop-furniture-showcase__chips" aria-label="Planning highlights">
            <li className="shop-furniture-showcase__chip shop-furniture-showcase__chip--blue">
              Compare prices
            </li>
            <li className="shop-furniture-showcase__chip shop-furniture-showcase__chip--green">
              Estimate your budget
            </li>
            <li className="shop-furniture-showcase__chip shop-furniture-showcase__chip--purple">
              Find similar furniture
            </li>
          </ul>
        </div>
    </section>
  );
}

export default ShopTheFurnitureShowcase;
